// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Map, AdvancedMarker, useMap, InfoWindow } from '@vis.gl/react-google-maps';

import {
  formatGuestRideStatus,
  formatDuration,
  getStatusChipColor,
  getRideDurationMinutes,
} from '../utils/guestRideUtils';

// TYPES ---------------------------------------------------------------- //

interface GuestRidesMapViewProps {
  rides: any[];
  loading: boolean;
}

// FIT BOUNDS HELPER -------------------------------------------------------- //

const MapFitBounds: React.FC<{ rides: any[] }> = ({ rides }) => {
  const map = useMap('guestrides-map');

  React.useEffect(() => {
    if (!map || !window.google?.maps || rides.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidPoint = false;

    rides.forEach((ride: any) => {
      if (ride.dropoff_lat && ride.dropoff_lng) {
        bounds.extend({ lat: Number(ride.dropoff_lat), lng: Number(ride.dropoff_lng) });
        hasValidPoint = true;
      }
      if (ride.pickup_lat && ride.pickup_lng) {
        bounds.extend({ lat: Number(ride.pickup_lat), lng: Number(ride.pickup_lng) });
        hasValidPoint = true;
      }
    });

    if (hasValidPoint) {
      map.fitBounds(bounds, 60);
    }
  }, [map, rides]);

  return null;
};

// COMPONENT ---------------------------------------------------------------- //

const GuestRidesMapView: React.FC<GuestRidesMapViewProps> = ({ rides }) => {
  const [selectedRide, setSelectedRide] = React.useState<any | null>(null);

  // Default center: Dallas, TX
  const defaultCenter = { lat: 32.7767, lng: -96.797 };

  return (
    <Box sx={{ height: 600, position: 'relative' }}>
      <Map
        id='guestrides-map'
        mapId='a1bb97db90b89ef9'
        defaultCenter={defaultCenter}
        defaultZoom={10}
        disableDefaultUI
        gestureHandling='cooperative'
        clickableIcons={false}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        style={{ width: '100%', height: '100%' }}
      >
        <MapFitBounds rides={rides} />

        {rides.map((ride: any) => {
          if (!ride.dropoff_lat || !ride.dropoff_lng) return null;

          return (
            <AdvancedMarker
              key={ride.id}
              position={{ lat: Number(ride.dropoff_lat), lng: Number(ride.dropoff_lng) }}
              onClick={() => setSelectedRide(ride)}
            />
          );
        })}

        {selectedRide && selectedRide.dropoff_lat && selectedRide.dropoff_lng && (
          <InfoWindow
            position={{ lat: Number(selectedRide.dropoff_lat), lng: Number(selectedRide.dropoff_lng) }}
            onCloseClick={() => setSelectedRide(null)}
          >
            <Box sx={{ minWidth: 220, p: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                  {selectedRide.guest_name || 'Unknown Guest'}
                </Typography>
                <Chip
                  size='small'
                  color={getStatusChipColor(selectedRide.status)}
                  label={formatGuestRideStatus(selectedRide.status)}
                  sx={{ textTransform: 'capitalize', ml: 1 }}
                />
              </Box>

              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
                <strong>Pickup:</strong> {selectedRide.pickup_address || '-'}
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                <strong>Dropoff:</strong> {selectedRide.dropoff_address || '-'}
              </Typography>

              {getRideDurationMinutes(selectedRide) != null ? (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                  <strong>Duration:</strong> {formatDuration(getRideDurationMinutes(selectedRide))}
                </Typography>
              ) : null}
            </Box>
          </InfoWindow>
        )}
      </Map>
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default GuestRidesMapView;
